import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {filter} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {ModeManagerService} from '../../editor-state/mode-manager.service';
import {MatDialog} from '@angular/material/dialog';
import {ErrorDialogComponent} from '../../editor-interface/dialogs/error-dialog/error-dialog.component';
import {RequestTask} from 'src/app/shared/models/request-task.interface';

const backendKnownErrors = ['attachment.record.not_found'];

@Injectable({
  providedIn: 'root'
})
export class RequestsService {
  private requestQueue = [];
  private queueIdleSubject = new BehaviorSubject<boolean>(true);
  private errorDialogOpen = false;

  constructor(
    private http: HttpClient,
    private modeManagerSvc: ModeManagerService,
    private translateSvc: TranslateService,
    private dialogSvc: MatDialog
  ) {}

  addToQueue(task: RequestTask, needResult?: boolean) {
    let resultPromise;

    if (needResult) {
      resultPromise = new Promise((resolve, reject) => {
        task.promise = {resolve, reject};
      });
    }

    this.requestQueue.push(task);
    this.queueIdleSubject.next(false);

    if (this.requestQueue.length === 1) {
      this.runQueue();
    }

    return resultPromise || Promise.resolve();
  }

  /** Resolves once every queued request has completed (used by the edit->view autosave flush). */
  whenIdle(): Promise<void> {
    return firstValueFrom(this.queueIdleSubject.pipe(filter(Boolean))).then(() => undefined);
  }

  private runQueue() {
    const task: RequestTask = this.requestQueue[0];

    const url = task.isApi ? `${environment.API_ENDPOINT}${task.url}` : task.url;

    const options: any = {body: task.data, params: task.queryParams};

    if (task.headers) {
      options.headers = task.headers;
    }

    this.http
      .request(task.method, url, options)
      .toPromise()
      .then(response => {
        if (task.promise) {
          task.promise.resolve(response);
        }

        this.next();
      })
      .catch(error => {
        this.handleQueueError(error, task);
      });
  }

  private next() {
    this.requestQueue.shift();

    if (this.requestQueue.length > 0) {
      this.runQueue();
    } else {
      this.queueIdleSubject.next(true);
    }
  }

  private handleQueueError(error, task) {
    if (error instanceof HttpErrorResponse) {
      const {status, http_code: statusCode} = error.error || {};

      if (statusCode === 404) {
        if (backendKnownErrors.includes(status)) {
          this.modeManagerSvc.goViewMode().then(() => this.openErrorDialog('POPUPS.EDIT_ERROR'));
        }
      } else if (status === 'diagram.err.locked') {
        // autosave rejected because another user holds the diagram lock: never drop this silently
        this.modeManagerSvc.goViewMode().then(() => this.openErrorDialog('POPUPS.SAVE_LOCKED'));
      }
    }

    if (task.promise) {
      task.promise.reject(error);
    }

    this.next();
  }

  private openErrorDialog(message: string): void {
    if (this.errorDialogOpen) {
      return;
    }

    this.errorDialogOpen = true;
    this.dialogSvc
      .open(ErrorDialogComponent, {
        panelClass: ['q9-dialog', 'q9-dialog-danger'],
        data: {message}
      })
      .afterClosed()
      .subscribe(() => (this.errorDialogOpen = false));
  }
}
